import { apiBase, accessToken } from '../turnover/helpers.js';

export { apiBase, accessToken };

export const PHONE_DIGITS = 11;
export const OTP_DIGITS = 6;
export const PRIVACY_NOTICE =
  "For privacy and security, Information can’t modified after confirmation. Please contact customer service .";

export function digitsOnly(value, max = PHONE_DIGITS) {
  return String(value || '').replace(/\D/g, '').slice(0, max);
}

export function normalizePhoneForApi(value) {
  return digitsOnly(value, PHONE_DIGITS);
}

/** Display-only. Does not change stored phone values. */
export function formatBdPhoneDisplay(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;
  let local = digits;
  if (local.startsWith('880') && local.length >= 12) {
    local = local.slice(3);
  }
  if (local.length === 11 && local.startsWith('0')) {
    return `+880 ${local.slice(1)}`;
  }
  if (local.length === 10 && local.startsWith('1')) {
    return `+880 ${local}`;
  }
  return raw;
}

/** Display-only. Does not change stored date values. */
export function formatYmd(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const iso = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (iso) {
    return `${iso[1]}/${iso[2].padStart(2, '0')}/${iso[3].padStart(2, '0')}`;
  }
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw) || /^\d{4}\/\d{2}\/\d{2}/.test(raw)) {
    const y = raw.slice(0, 4);
    const mo = raw.slice(5, 7);
    const d = raw.slice(8, 10);
    return `${y}/${mo}/${d}`;
  }
  const y = dt.getUTCFullYear();
  const mo = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}/${mo}/${d}`;
}

export function sessionUser() {
  try {
    const raw = localStorage.getItem('ninet6_player_session');
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.user || {};
  } catch {
    return {};
  }
}

export function pickUsername(profile, user = sessionUser()) {
  return String(profile?.username || user?.username || '').trim();
}

export function pickVipRankName(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return '';
  return String(
    snapshot.rankName
    || snapshot.currentRank?.rankName
    || snapshot.currentTier?.label
    || '',
  ).trim();
}

export function hasVipCoinValue(wallet) {
  return Boolean(wallet) && Object.prototype.hasOwnProperty.call(wallet, 'vipCoin');
}

export function formatVipPoints(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

export function primaryPhone(phones) {
  const list = Array.isArray(phones) ? phones : [];
  return list.find((row) => row?.isPrimary) || list[0] || null;
}

export function realEmails(emails) {
  return (Array.isArray(emails) ? emails : [])
    .map((row) => ({
      id: row?.id || '',
      value: String(row?.value || '').trim(),
      verified: Boolean(row?.verified),
    }))
    .filter((row) => row.value);
}

function unwrap(json) {
  return json?.data !== undefined ? json.data : json;
}

async function playerRequest(path, { method = 'GET', body } = {}) {
  const token = accessToken();
  const res = await fetch(`${apiBase()}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    cache: 'no-store',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.message || json.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return unwrap(json);
}

export async function fetchPlayerProfile() {
  return playerRequest('/profile');
}

export async function patchProfileField(field, value) {
  return playerRequest('/profile', { method: 'PATCH', body: { field, value } });
}

export async function postProfilePhone(phone) {
  return playerRequest('/profile/phones', { method: 'POST', body: { phone } });
}

export async function patchProfilePhone(phoneId, phone) {
  return playerRequest(`/profile/phones/${encodeURIComponent(phoneId)}`, {
    method: 'PATCH',
    body: { phone },
  });
}

export async function sendProfileOtp(payload) {
  return playerRequest('/profile/otp/send', { method: 'POST', body: payload });
}

export async function verifyProfileOtp(payload) {
  return playerRequest('/profile/otp/verify', { method: 'POST', body: payload });
}

export async function fetchPlayerWallet() {
  return playerRequest('/wallet');
}

export function validatePhoneInput(value) {
  const normalized = normalizePhoneForApi(value);
  if (!normalized) return { ok: false, reason: 'invalid' };
  if (normalized.length !== PHONE_DIGITS) return { ok: false, reason: 'invalid' };
  return { ok: true, value: normalized };
}
